// CRM link (Find walk-through K-03, 19 Oct 2026, items 32 and 33; docs/specs/FIND_WALKTHROUGH_2026_10.md).
// Ogma checks every firm against the CRM for earlier conversations before anything is sent: the same person who has
// replied or is in a conversation is never cold-contacted; a colleague at a firm already in touch waits for the
// owner's answer ("Already in touch with Jo Smith here. Contact Sam anyway?", task kind in_touch_check, step 4.10).
// Only K-03 edits this file.
import type { Crm } from "./crm";
import type { HumanTask } from "./types";

/** crm_cold_contact_summary: the counts behind the one CRM link line. */
export interface ColdContactSummary {
  /** The business has told Ogma who it knows (a contact file, its other CRM, its sent mail or past conversations). */
  on: boolean;
  /** Addresses, firm websites and company numbers on the known list. */
  known: number;
  /** People marked known or with past conversations. */
  people_known: number;
  /** Firms someone wrote in from within the conversation window (12 months by default). */
  in_touch_firms: number;
  /** "Contact anyway?" questions waiting for the owner. */
  waiting: number;
  /** People kept out of cold outreach in the last 30 days. */
  kept_out_30d: number;
  /** People the owner said to contact anyway. */
  said_yes: number;
}

export const IN_TOUCH_KIND = "in_touch_check";
/** Where Your contacts lives now: the CRM (the old Find address still opens it). */
export const CONTACTS_PATH = "/settings/data";

const EMPTY: ColdContactSummary = { on: false, known: 0, people_known: 0, in_touch_firms: 0, waiting: 0, kept_out_30d: 0, said_yes: 0 };

export async function coldContactSummary(crm: Crm): Promise<ColdContactSummary> {
  const r = await crm.read<Partial<ColdContactSummary> | null>("crm_cold_contact_summary", {});
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
  if (!r) return EMPTY;
  return {
    on: r.on === true,
    known: n(r.known),
    people_known: n(r.people_known),
    in_touch_firms: n(r.in_touch_firms),
    waiting: n(r.waiting),
    kept_out_30d: n(r.kept_out_30d),
    said_yes: n(r.said_yes),
  };
}

/** Yes: contact this one person anyway. No: leave them out (their cold sequence stops). */
export const answerInTouch = (crm: Crm, task: string, contact: boolean) =>
  crm.write<{ contact: boolean; person_id: string; enrolments_stopped: number }>("answer_in_touch", { task, contact });

/** "Already in touch with Jo Smith at Talking Ltd. Contact Sam anyway?" from the task's payload. */
export function inTouchQuestion(t: Pick<HumanTask, "payload">): string {
  const p = t.payload ?? {};
  if (typeof p.summary === "string" && p.summary) return p.summary;
  const first = String(p.person_name ?? "").split(" ")[0] || "this person";
  return `Already in touch with ${String(p.with_name ?? "someone")} at ${String(p.organisation_name ?? "this firm")}. Contact ${first} anyway?`;
}
